import React from 'react';
import { Link } from 'react-router-dom';
import type { AuditLogEntry } from '../types/audit-log.types';
import {
  formatFullUtc,
  formatLocalDateTime,
  getActionLabel,
  getResourceLink,
} from '../utils/audit-log-utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';

interface AuditLogRowProps {
  entry: AuditLogEntry;
}

export const AuditLogRow: React.FC<AuditLogRowProps> = ({ entry }) => {
  const resourceLink = getResourceLink(entry);
  const actionLabel = getActionLabel(entry.action);

  const renderActorBadge = () => {
    if (entry.actorType === 'client') {
      return (
        <span className="ml-1.5 inline-flex items-center rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600">
          API client
        </span>
      );
    }
    if (entry.actorType === 'system') {
      return (
        <span className="ml-1.5 inline-flex items-center rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600">
          System
        </span>
      );
    }
    return null;
  };

  return (
    <tr className="border-b border-[#F4F5F6] hover:bg-neutral-50/60 transition-colors">
      {/* Actor */}
      <td className="px-4 py-3 align-middle text-neutral-900 text-sm">
        <span className="font-medium">{entry.actorLabel || 'System'}</span>
        {renderActorBadge()}
      </td>

      {/* Action */}
      <td className="px-4 py-3 align-middle text-sm">
        <div className="flex flex-col">
          <span className="font-medium text-neutral-900">{actionLabel}</span>
          <code className="text-caption text-neutral-500 font-mono">{entry.action}</code>
        </div>
      </td>

      {/* Resource */}
      <td className="px-4 py-3 align-middle text-neutral-600 text-sm whitespace-nowrap">
        <span className="capitalize">{entry.resourceType}</span>
        <span className="mx-1 text-neutral-400">·</span>
        {resourceLink ? (
          <Link
            to={resourceLink}
            className="text-primary font-mono text-caption hover:underline"
            title={`View ${entry.resourceType} ${entry.resourceId}`}
          >
            {entry.resourceId}
          </Link>
        ) : (
          <span className="font-mono text-caption text-neutral-700">{entry.resourceId}</span>
        )}
      </td>

      {/* Summary */}
      <td className="px-4 py-3 align-middle text-neutral-900 text-sm">
        <span>{entry.summary}</span>
      </td>

      {/* Time */}
      <td className="px-4 py-3 align-middle text-neutral-600 text-sm whitespace-nowrap">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help hover:text-neutral-900 transition-colors">
                {formatLocalDateTime(entry.occurredAt)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <span>{formatFullUtc(entry.occurredAt)}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>
    </tr>
  );
};
